/**
 * Grafo genérico representado como lista de adyacencia con pesos.
 * Soporta grafos dirigidos y no dirigidos, con aristas ponderadas opcionales.
 * Incluye recorridos BFS y DFS.
 * @template T - El tipo de los vértices. Debe ser usable como clave de Map.
 */
export class Graph<T> {
  readonly #directed: boolean
  readonly #adjacency = new Map<T, Map<T, number>>()
  #edgeCount = 0

  /**
   * @param directed - true para grafo dirigido (por defecto), false para no dirigido.
   */
  constructor(directed = true) {
    this.#directed = directed
  }

  /**
   * Agrega un vértice al grafo. No hace nada si ya existe.
   */
  addVertex(v: T): void {
    if (!this.#adjacency.has(v)) {
      this.#adjacency.set(v, new Map())
    }
  }

  /**
   * Elimina un vértice y todas sus aristas incidentes.
   * @returns true si el vértice existía, false si no.
   */
  removeVertex(v: T): boolean {
    if (!this.#adjacency.has(v)) return false
    this.#adjacency.delete(v)
    for (const [, neighbors] of this.#adjacency) {
      neighbors.delete(v)
    }
    this.#recomputeEdgeCount()
    return true
  }

  /**
   * Agrega una arista de u hacia v con peso opcional (por defecto 1).
   * En grafos no dirigidos, también agrega v → u con el mismo peso.
   * Crea los vértices automáticamente si no existen.
   */
  addEdge(u: T, v: T, weight = 1): void {
    this.addVertex(u)
    this.addVertex(v)
    const isNew = !this.#adjacency.get(u)!.has(v)
    this.#adjacency.get(u)!.set(v, weight)
    if (!this.#directed) {
      this.#adjacency.get(v)!.set(u, weight)
    }
    if (isNew) this.#edgeCount++
  }

  /**
   * Elimina la arista de u a v.
   * En grafos no dirigidos, también elimina v → u.
   * @returns true si la arista existía, false si no.
   */
  removeEdge(u: T, v: T): boolean {
    const neighbors = this.#adjacency.get(u)
    if (!neighbors?.has(v)) return false
    neighbors.delete(v)
    this.#edgeCount--
    if (!this.#directed) {
      this.#adjacency.get(v)?.delete(u)
    }
    return true
  }

  /**
   * Indica si el vértice existe en el grafo.
   */
  hasVertex(v: T): boolean {
    return this.#adjacency.has(v)
  }

  /**
   * Indica si existe una arista de u a v.
   */
  hasEdge(u: T, v: T): boolean {
    return this.#adjacency.get(u)?.has(v) ?? false
  }

  /**
   * Retorna los vértices adyacentes a v (destinos de sus aristas salientes).
   */
  neighbors(v: T): T[] {
    return [...(this.#adjacency.get(v)?.keys() ?? [])]
  }

  /**
   * Retorna el peso de la arista u → v, o undefined si no existe.
   */
  weight(u: T, v: T): number | undefined {
    return this.#adjacency.get(u)?.get(v)
  }

  /**
   * Recorrido en anchura (BFS) desde el vértice start.
   * Visita primero los vértices más cercanos.
   * @returns Array de vértices en orden de visita. Vacío si start no existe.
   */
  bfs(start: T): T[] {
    if (!this.#adjacency.has(start)) return []
    const visited = new Set<T>([start])
    const queue: T[] = [start]
    const result: T[] = []
    while (queue.length > 0) {
      const vertex = queue.shift()!
      result.push(vertex)
      for (const neighbor of this.#adjacency.get(vertex)!.keys()) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor)
          queue.push(neighbor)
        }
      }
    }
    return result
  }

  /**
   * Recorrido en profundidad (DFS) desde el vértice start.
   * Explora cada rama hasta el fondo antes de retroceder.
   * @returns Array de vértices en orden de visita. Vacío si start no existe.
   */
  dfs(start: T): T[] {
    if (!this.#adjacency.has(start)) return []
    const visited = new Set<T>()
    const stack: T[] = [start]
    const result: T[] = []
    while (stack.length > 0) {
      const vertex = stack.pop()!
      if (visited.has(vertex)) continue
      visited.add(vertex)
      result.push(vertex)
      const adj = [...this.#adjacency.get(vertex)!.keys()].reverse()
      for (const neighbor of adj) {
        if (!visited.has(neighbor)) stack.push(neighbor)
      }
    }
    return result
  }

  /**
   * Número de vértices en el grafo.
   */
  get vertexCount(): number {
    return this.#adjacency.size
  }

  /**
   * Número de aristas en el grafo.
   * En grafos no dirigidos, cada arista se cuenta una sola vez.
   */
  get edgeCount(): number {
    return this.#edgeCount
  }

  /**
   * Indica si el grafo está vacío (sin vértices).
   */
  isEmpty(): boolean {
    return this.#adjacency.size === 0
  }

  /**
   * Indica si el grafo es dirigido.
   */
  get directed(): boolean {
    return this.#directed
  }

  /**
   * Vacía el grafo completamente.
   */
  clear(): void {
    this.#adjacency.clear()
    this.#edgeCount = 0
  }

  #recomputeEdgeCount(): void {
    let count = 0
    for (const [, neighbors] of this.#adjacency) {
      count += neighbors.size
    }
    this.#edgeCount = this.#directed ? count : count / 2
  }
}
